import { useEffect, useRef, useState } from 'react';

// Speak a phone number instead of typing it: the browser's own speech
// recognition (Chrome, Android, Safari on iPhone — through Google's or
// Apple's servers), in Greek, turned into digits only. Where the browser has
// none, `supported` is false and callers hide their microphone button.

const Recognition =
	typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

export const speechSupported = !!Recognition;

const UNITS = {
	μηδεν: 0,
	ενα: 1,
	ενας: 1,
	μια: 1,
	δυο: 2,
	δυω: 2,
	τρια: 3,
	τρεις: 3,
	τεσσερα: 4,
	τεσσερις: 4,
	πεντε: 5,
	εξι: 6,
	επτα: 7,
	εφτα: 7,
	οκτω: 8,
	οχτω: 8,
	εννεα: 9,
	εννια: 9,
};
const TEENS = {
	δεκα: 10,
	εντεκα: 11,
	δωδεκα: 12,
	δεκατρια: 13,
	δεκατεσσερα: 14,
	δεκαπεντε: 15,
	δεκαεξι: 16,
	δεκαεπτα: 17,
	δεκαεφτα: 17,
	δεκαοκτω: 18,
	δεκαοχτω: 18,
	δεκαεννεα: 19,
	δεκαεννια: 19,
};
const TENS = {
	εικοσι: 2,
	τριαντα: 3,
	σαραντα: 4,
	πενηντα: 5,
	εξηντα: 6,
	εβδομηντα: 7,
	ογδοντα: 8,
	ενενηντα: 9,
};

const strip = s => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// "697 12 34" / "έξι εννιά επτά" / "εξήντα εννιά επτά" → "697…". Digits the
// recogniser already wrote are kept; Greek number words become digits
// ("εξήντα εννιά" = 69, "εξήντα" alone = 60); everything else is dropped.
export const spokenToDigits = text => {
	const tokens = strip(text || '')
		.replace(/[^0-9a-zα-ω\s]/g, ' ')
		.split(/\s+/)
		.filter(Boolean);
	let out = '';
	for (let i = 0; i < tokens.length; i++) {
		const t = tokens[i];
		if (/^\d+$/.test(t)) out += t;
		else if (t in TEENS) out += String(TEENS[t]);
		else if (t in TENS) {
			const next = tokens[i + 1];
			if (next in UNITS && UNITS[next] > 0) {
				out += `${TENS[t]}${UNITS[next]}`;
				i++;
			} else out += `${TENS[t]}0`;
		} else if (t in UNITS) out += String(UNITS[t]);
	}
	return out.slice(0, 15);
};

export const useSpeechDigits = onDigits => {
	const [listening, setListening] = useState(false);
	const recRef = useRef(null);
	const cbRef = useRef(onDigits);
	useEffect(() => {
		cbRef.current = onDigits;
	}, [onDigits]);

	useEffect(() => () => recRef.current?.abort(), []);

	const stop = () => recRef.current?.stop();

	const start = () => {
		if (!Recognition || recRef.current) return;
		const rec = new Recognition();
		rec.lang = 'el-GR';
		rec.interimResults = true;
		rec.continuous = false;
		rec.maxAlternatives = 1;
		rec.onresult = e => {
			const text = Array.from(e.results)
				.map(r => r[0]?.transcript || '')
				.join(' ');
			const digits = spokenToDigits(text);
			if (digits) cbRef.current?.(digits);
		};
		rec.onend = () => {
			recRef.current = null;
			setListening(false);
		};
		rec.onerror = () => {
			recRef.current = null;
			setListening(false);
		};
		recRef.current = rec;
		setListening(true);
		try {
			rec.start();
		} catch {
			recRef.current = null;
			setListening(false);
		}
	};

	return { supported: speechSupported, listening, start, stop };
};
